/**
 * **变参行的拖拽排序** —— 从 `components/NpNode.vue` 向下拆出来的交互块（约 100 行）。
 *
 * ## 为什么按这里切
 *
 * `NpNode.vue` 的 script 里有**五组互不相干的指针交互**（就地改名 / 行实例改名 /
 * 变参行排序 / 面板拉宽 / 框·注释改尺寸）。量下来这一组的外部依赖最少：
 *
 * ```text
 *   本文件声明：2 个 ref（待定 + 正在拖）
 *   用到组件内的：只 emit 一个事件（repeater-reorder）
 *   其他：Math / DOM（getBoundingClientRect、指针捕获）
 * ```
 *
 * 所以它是**几乎零耦合**的一块 —— 按耦合拆，不按行数。
 *
 * ## ⚠️ 这块里有一条**踩过坑才写对**的规矩，别改回去
 *
 * **`pointerdown` 时不能立刻 `setPointerCapture`。**
 *
 * 指针捕获会把后续点击的目标都改到那个元素上 —— 双击就再也拼不起来
 * （两次 `click` 落在不同目标上）。这正是「变参行双击改不了名」的根因，
 * 而且只在**已连接**的行上出现。
 *
 * 所以流程是：
 * ```text
 *   pointerdown  → 只记「待定」（连阈值都还没过）
 *   pointermove  → 位移 > 5px 才真的开始拖 + 捕获
 *   pointerup    → 松捕获；from ≠ to 才发 repeater-reorder
 * ```
 * 点一下不动 = 普通点击（双击正常触发），拖开 = 排序。
 *
 * 屏幕行高（`rowScreenHeight`）也是同一条思路的产物：
 * **测量而不是索取图引擎的 zoom** —— 屏幕位移 / 屏幕行高 = 移动了几行。
 *
 * ## 归属
 *
 * **向下**：`NpNode.vue` → `composables/useRepeaterDrag.ts`，只有前者引用它。
 */
import { ref } from 'vue'
import type { Ref } from 'vue'
import type { RenderItem } from '@/node/rows'

interface RepeaterDragState {
  rowId: string
  from: number
  to: number
  startY: number
  /**
   * 这一行在**屏幕**上的高度。
   *
   * 用它换算位移就自动抵消了画布缩放 ——
   * 屏幕位移 / 屏幕行高 = 移动了几行，不需要去问图引擎 zoom 是多少。
   * （和拉宽手柄同一个思路：测量，而不是索取状态。）
   */
  rowScreenHeight: number
  total: number
}

export interface RepeaterDragCtx {
  /** 拖完把 `from → to` 抛出去（组件那边接成 `repeater-reorder`） */
  reorder: (rowId: string, from: number, to: number) => void
}

export function useRepeaterDrag(ctx: RepeaterDragCtx) {
  /**
   * 「按下但还没拖开」的待定状态。
   *
   * 存在的唯一理由是**别在 pointerdown 就捕获指针** —— 那会让双击失效。
   */
  const repeaterDragPending = ref<{
    rowId: string
    from: number
    startY: number
    startX: number
    rowScreenHeight: number
    total: number
    el: HTMLElement
    pointerId: number
  } | null>(null)

  /** 正在拖的那一行（模板拿它算 `dragging` / `drop-target` 两个样式） */
  const repeaterDrag: Ref<RepeaterDragState | null> = ref(null)

  function onRepeaterDragStart(item: RenderItem, ev: PointerEvent) {
    if (ev.button !== 0 || item.kind !== 'repeater-row') return
    // 只有已连线的实例可拖 —— 拖动空行没有意义，也没有东西可以移动
    if (!item.socket.connected) return

    // 端口区域属于连线手势，不参与排序
    const target = ev.target as HTMLElement | null
    if (target?.closest?.('.np-socket')) return

    /*
     * ⚠️ 这里**不能**立刻 setPointerCapture（理由见文件头）。
     * 先记成「待定」，等真的移动超过阈值再开始拖 + 捕获。
     */
    const el = ev.currentTarget as HTMLElement
    repeaterDragPending.value = {
      rowId: item.row.id,
      from: item.index,
      startY: ev.clientY,
      startX: ev.clientX,
      rowScreenHeight: el.getBoundingClientRect().height || 1,
      total: item.total,
      el,
      pointerId: ev.pointerId,
    }
  }

  function onRepeaterDragMove(ev: PointerEvent) {
    // 还没开始拖：移动超过阈值才算「真的要排序」
    const pending = repeaterDragPending.value
    if (pending && !repeaterDrag.value) {
      if (Math.hypot(ev.clientX - pending.startX, ev.clientY - pending.startY) < 5) return
      repeaterDrag.value = {
        rowId: pending.rowId,
        from: pending.from,
        to: pending.from,
        startY: pending.startY,
        rowScreenHeight: pending.rowScreenHeight,
        total: pending.total,
      }
      pending.el.setPointerCapture?.(pending.pointerId)
      repeaterDragPending.value = null
    }

    const drag = repeaterDrag.value
    if (!drag) return
    const steps = Math.round((ev.clientY - drag.startY) / drag.rowScreenHeight)
    const to = Math.min(Math.max(drag.from + steps, 0), drag.total - 1)
    if (to !== drag.to) drag.to = to
  }

  function onRepeaterDragEnd(ev: PointerEvent) {
    repeaterDragPending.value = null
    const drag = repeaterDrag.value
    if (!drag) return
    ;(ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId)
    repeaterDrag.value = null
    if (drag.from !== drag.to) {
      ctx.reorder(drag.rowId, drag.from, drag.to)
    }
  }

  return { repeaterDrag, onRepeaterDragStart, onRepeaterDragMove, onRepeaterDragEnd }
}
